import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    School as SchoolIcon
} from '@mui/icons-material';
import { localizeClassItem, formatClassPrice } from '../../config/classUiConfig';
import './PaymentsDueSection.css';

const PaymentsDueSection = ({ payments = [] }) => {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(dateLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'success';
            case 'waived':
                return 'info';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        const key = `paymentsDue.statusLabels.${status}`;
        const translated = t(key);
        return translated !== key ? translated : status;
    };

    const getLocalizedClassName = (name) => {
        if (!name) return name;
        return localizeClassItem({ title: name }, i18n.language).title;
    };

    const formatAmount = (amount) => formatClassPrice(amount, i18n.language);

    return (
        <Box className="payments-due-section">
            <Typography variant="h6" component="h2" gutterBottom>
                {t('paymentsDue.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('paymentsDue.offlineHelp')}
            </Typography>

            {payments.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('paymentsDue.class')}</TableCell>
                                <TableCell>{t('paymentsDue.session')}</TableCell>
                                <TableCell>{t('paymentsDue.amount')}</TableCell>
                                <TableCell>{t('paymentsDue.method')}</TableCell>
                                <TableCell>{t('paymentsDue.status')}</TableCell>
                                <TableCell>{t('paymentsDue.paidDate')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payments.map((payment) => (
                                <TableRow key={payment.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SchoolIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {getLocalizedClassName(payment.class_name)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {formatDate(payment.session_date)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {formatAmount(payment.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {payment.payment_method || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(payment.status)}
                                            color={getStatusColor(payment.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {payment.status === 'paid' && payment.paid_at
                                                ? formatDate(payment.paid_at)
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {t('paymentsDue.empty')}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default PaymentsDueSection;
